import {createGamingTelemetry,type GamingTelemetrySink} from './telemetry';
import { programFrameSourceId } from '@ohmyprime/segmenter';
import type { GamingControlProvider, SwarmPublisher } from '../net/api';
import type { components } from './contracts';
import type { GamingController } from './controller';
import { measureSourceClock } from './source-clock';

type Receipt = components['schemas']['PublicationReceipt'];
type State = { readonly status: 'idle' | 'pending' | 'failed' | 'received' | 'server-observed'; readonly receipt?: Receipt; readonly observation?: components['schemas']['ReturnMarkerObservation'] };
type Publisher = Pick<SwarmPublisher, 'getWitnessIdentity' | 'beginWitnessUpload'>;
const LEDGER = 'perck.gaming.ending-publications';
const LIMIT = 64;

/** Owns control bookkeeping only; never stops, replaces or waits on ordinary media. */
export class EndingPublication {
  private readonly consumed = new Set<string>();
  private readonly unsubscribe: () => void;
  private readonly now: () => number;
  private usable = true;
  private disposed = false;
  private state: State = { status: 'idle' };
  private active: { current(): boolean; cancel(): void } | undefined;
  constructor(private readonly options: {
    telemetry?:GamingTelemetrySink;
    controller: GamingController;
    provider: GamingControlProvider;
    publisher(): Publisher;
    enabled(): boolean;
    restoredProgram?():import('./creator-program').CreatorProgram['restoredProgram']|undefined;
    scene(): string | null;
    track(): MediaStreamTrack | null;
    stream(): string | null;
    storage: Pick<Storage, 'getItem' | 'setItem'>;
    now?: () => number;
  }) {
    this.now = options.now ?? (() => performance.now());
    try { this.readLedger(); } catch { this.usable = false; }
    this.unsubscribe = options.controller.subscribe(() => this.sync());
    this.sync();
  }
  private readLedger(): void {
    const raw = this.options.storage.getItem(LEDGER);
    const entries: unknown = raw === null ? [] : JSON.parse(raw);
    if (!Array.isArray(entries) || entries.length > LIMIT || entries.some(k => typeof k !== 'string' || k.length > 1024)) throw Error('invalid_ledger');
    entries.forEach(k => this.consumed.add(k));
  }
  getState(): State { return this.state; }
  dispose(): void {
    this.disposed = true;
    this.unsubscribe();
    this.active?.cancel();
  }
  sync(): void {
    try { this.reconcile(); } catch { this.active?.cancel(); this.state = { status: 'failed' }; }
  }
  private reconcile(): void {
    // Once observed invalid, an attempt cannot revive if scene/flag/stream returns.
    this.active?.current();
    const o = this.options, session = o.controller.getState().session;
    if (this.disposed || !this.usable || !o.enabled() || !session || session.state !== 'ENDING' || session.endReason !== 'normal' || session.tickStatus !== 'confirmed' ||
        !Number.isSafeInteger(session.finalTick) || session.finalTick! < 0 || !Number.isSafeInteger(session.endingDeadlineMs) || session.endingDeadlineMs! <= Date.now()) return;
    const restored=o.restoredProgram?.(),restoredFrame=restored?.observation();
    if(o.restoredProgram&&(!restored?.current()||!restoredFrame))return;
    const key = JSON.stringify([session.sessionId, session.streamId, session.epoch]);
    if (this.consumed.has(key) || this.consumed.size >= LIMIT) return;
    // Read and persist synchronously before start, including another mount in this tab.
    // Storage is only a local replay guard; the backend remains the receipt authority.
    try {
      this.readLedger();
      if (this.consumed.has(key) || this.consumed.size >= LIMIT) return;
      this.consumed.add(key);
      o.storage.setItem(LEDGER, JSON.stringify([...this.consumed]));
    } catch { this.usable = false; this.state = { status: 'failed' }; return; }
    this.active?.cancel();
    const pinned = Object.freeze({ ...session }), publisher = o.publisher();
    const observed = publisher.getWitnessIdentity?.();
    const identity = observed && Object.freeze({ ...observed }), scene = o.scene(), track = o.track();
    const initial = this.now(), remaining = Math.min(5000,session.endingDeadlineMs! - Date.now()), deadline = initial + remaining;
    this.state = { status: 'pending' };
    let markerPending = false;
    let valid = true, timer: ReturnType<typeof setTimeout> | undefined;
    let reject!: (reason: Error) => void;
    const cancelled = new Promise<never>((_, fail) => { reject = fail; });
    // Cancellation can precede the first bounded await (missing prerequisites).
    void cancelled.catch(() => undefined);
    const cancel = () => {
      if (!valid) return;
      valid = false; clearTimeout(timer);
      this.state = { status: 'failed' };
      reject(Error('ending_publication_unavailable'));
    };
    const current = () => {
      if (!valid) return false;
      try {
        const next = o.controller.getState().session, nextIdentity = publisher.getWitnessIdentity?.(), time = this.now();
        if ((restored&&(!restored.current()||restored.observation()?.composition?.binding.generation!==restoredFrame?.composition?.binding.generation)) || this.disposed || !o.enabled() || !o.controller.enabled || !next || next.state !== 'ENDING' ||
            next.sessionId !== pinned.sessionId || next.epoch !== pinned.epoch || (next.revision !== pinned.revision && !(markerPending && next.revision === pinned.revision + 1)) || next.streamId !== pinned.streamId ||
            o.stream() !== pinned.streamId || o.publisher() !== publisher || !scene || o.scene() !== scene ||
            !track || o.track() !== track || track.readyState === 'ended' || !identity || !nextIdentity || identity.streamId !== pinned.streamId ||
            identity.track !== track || programFrameSourceId(track) !== identity.sourceId ||
            !Object.entries(identity).every(([k,v]) => nextIdentity[k as keyof typeof identity] === v) ||
            !Number.isFinite(time) || time < initial || time >= deadline) cancel();
      } catch { cancel(); }
      return valid;
    };
    const active = { current, cancel };
    this.active = active;
    const bounded = async <T>(operation: () => Promise<T>): Promise<T> => {
      if (!current()) throw Error('ending_publication_unavailable');
      const result = await Promise.race([operation(), cancelled]);
      if (!current()) throw Error('ending_publication_unavailable');
      return result;
    };
    timer = setTimeout(cancel, remaining);
    void (async () => {
      try {
        const clock = await bounded(() => measureSourceClock({ provider: o.provider, session: pinned, track: o.track,
          enabled: true, current, deadlineMonotonicMs: deadline, now: this.now }));
        const outcome = await bounded(() => publisher.beginWitnessUpload!({ enabled: true, session: pinned, identity: identity!, clock, current, deadlineMonotonicMs: deadline }));
        if (outcome.status !== 'uploaded') throw Error('upload_unavailable');
        const upload = outcome.upload;
        if (upload.status !== 'uploaded' || upload.streamId !== pinned.streamId || upload.broadcastId !== identity!.broadcastId ||
            upload.keyId !== identity!.keyId || upload.generation !== identity!.generation || upload.renditionId !== 'main' ||
            !Number.isSafeInteger(upload.seq) || upload.seq < 0 || !Number.isSafeInteger(upload.uploadId) || upload.uploadId <= 0) throw Error('upload_changed');
        const receipt = await bounded(() => o.provider.publicationReceipt!(pinned.sessionId, pinned.streamId, {
          epoch: pinned.epoch, revision: pinned.revision, sourceId: identity!.sourceId,
          ...(outcome.composition?{composition:outcome.composition}:{}),
          broadcastId: identity!.broadcastId, keyId: identity!.keyId, renditionId: 'main', seq: upload.seq,
        }, current));
        const wall = Date.now();
        if (receipt.sessionId !== pinned.sessionId || receipt.epoch !== pinned.epoch || receipt.revision !== pinned.revision ||
            !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(receipt.receiptId) ||
            !Number.isFinite(receipt.expiresAtMs) || receipt.expiresAtMs <= wall || receipt.expiresAtMs > wall + 5000) throw Error('receipt_changed');
        this.state = { status: 'received', receipt: Object.freeze({ ...receipt }) };
        if (!o.provider.recordMarker) return;
        markerPending = true;
        const context = { operationId: crypto.randomUUID(), deadlineMs: Math.min(receipt.expiresAtMs, pinned.endingDeadlineMs!), current };
        const body = {epoch:pinned.epoch, revision:pinned.revision, receiptId:receipt.receiptId};
        let observation: components['schemas']['ReturnMarkerObservation'];
        try { observation = await bounded(() => o.provider.recordMarker!(pinned.sessionId, pinned.streamId, body, context)); }
        catch {
          // Exactly one bounded retry, using the original receipt/revision/operation identity.
          observation = await bounded(() => o.provider.recordMarker!(pinned.sessionId, pinned.streamId, body, context));
        }
        if (observation.sessionId !== pinned.sessionId || observation.epoch !== pinned.epoch ||
            observation.revision !== pinned.revision + 1 || observation.receiptId !== receipt.receiptId ||
            observation.mediaGeneration !== identity!.keyId || observation.marker.streamId !== pinned.streamId || observation.marker.mediaGeneration !== identity!.keyId ||
            observation.expiresAtMs <= Date.now() || observation.expiresAtMs > receipt.expiresAtMs) throw Error('marker_changed');
        createGamingTelemetry(o.telemetry)('studio-marker',pinned);
        this.state = {status:'server-observed', receipt:Object.freeze({...receipt}), observation:Object.freeze({...observation})};
      } catch { cancel(); }
      finally {
        clearTimeout(timer);
        // No active work or authority survives completion; retained receipt is metadata only.
        valid = false;
        if (this.active === active) this.active = undefined;
      }
    })();
  }
}
